import React, { useState, useEffect, useMemo } from 'react';
import { useDarkMode } from 'utils/darkMode';
import { useTranslation } from 'utils/i18n';
import { getApiClient } from 'utils/apiClient';
import { MarketplaceStepIndicator } from './MarketplaceStepIndicator';
import { BasicsTab } from './tabs/BasicsTab';
import { ReviewTab } from './tabs/ReviewTab';
import marketplaceTranslations from './translations';
import '../ResourceDetailModal/styles/ResourceConfiguration.css';

/**
 * Step 2: Resource Configuration
 * Wizard-style interface matching bot management wizard template
 */
export const ResourceConfiguration = ({
  formData,
  handleInputChange,
  selectedProduct,
  selectedPrice,
  prices = [],
  tenants,
  loadingTenants,
  dropdownOpen,
  setDropdownOpen,
  setFormData,
  setIsSelectedTenantPartner,
  setIsDevTenant,
  loadCostPools,
  costPools,
  loadingCostPools,
  isDevTenant,
  isSelectedTenantPartner,
  isDevResource,
  isCurrentProductFree,
  isCostPoolRequired,
  setIsDevResource,
  mode,
  isFormValid,
  onBack,
  onSubmit,
  setStepTitle,
  setStepDescription,
  onClose,
  mainWizardStep = 2,
  setCurrentWizardStep,
  loadingPrices = false,
  handleSubscriptionSelect = null,
  loadPrices = null,
  loading = false
}) => {
  const { isDarkMode } = useDarkMode();
  const { language } = useTranslation();
  const t = (key, defaultValue = '') => {
    const keys = key.split('.');
    let value = marketplaceTranslations[language] || marketplaceTranslations.en;
    for (const k of keys) {
      value = value?.[k];
    }
    return value || defaultValue || key;
  };
  const [currentStep, setCurrentStep] = useState(0);
  const [tenantIdsWithResale, setTenantIdsWithResale] = useState([]);

  // When creating a resale resource, load which tenants already have a resale (only one per tenant)
  useEffect(() => {
    const isResale =
      formData.configuration?.is_resale_product === true ||
      formData.type === 'resale' ||
      selectedProduct?.metadata?.resource_type === 'resale' ||
      !!selectedProduct?.metadata?.from_resale_resource;
    if (!isResale || !tenants?.length) {
      setTenantIdsWithResale([]);
      return;
    }
    let cancelled = false;
    const apiClient = getApiClient();
    Promise.all(
      tenants.map(async (t) => {
        try {
          const res = await apiClient.listResources({ workspaceId: t.id });
          const data = res?.data || [];
          const hasResale = data.some((r) => r.type === 'resale');
          return hasResale ? t.id : null;
        } catch (error) {
          const status = error?.status || error?.response?.status;
          if (status !== 403) {
            console.warn('Failed to check existing resale resources for tenant', { tenant_id: t.id, status });
          }
          return null;
        }
      })
    ).then((ids) => {
      if (!cancelled) {
        setTenantIdsWithResale(ids.filter(Boolean));
      }
    });
    return () => { cancelled = true; };
  }, [formData.configuration?.is_resale_product, formData.type, selectedProduct?.metadata?.resource_type, selectedProduct?.metadata?.from_resale_resource, tenants]);

  // Update step title/description when internal step changes
  useEffect(() => {
    if (setStepTitle && setStepDescription) {
      const stepTitles = [
        t('steps.configuration', 'Configuration'),
        t('steps.review', 'Review')
      ];
      const stepDescriptions = [
        t('header.configurationDescription', 'Tailor Your Resource to Your Needs'),
        t('resourceConfiguration.readyToDeploy', 'Ready to Deploy')
      ];
      
      if (currentStep < stepTitles.length) {
        setStepTitle(stepTitles[currentStep]);
        setStepDescription(stepDescriptions[currentStep]);
        // Update wizard step: 0 = Configuration (step 2), 1 = Review (step 3)
        if (setCurrentWizardStep) {
          setCurrentWizardStep(currentStep === 1 ? 3 : 2);
        }
      }
    }
  }, [currentStep, setStepTitle, setStepDescription, setCurrentWizardStep]);
  
  // eslint-disable-next-line no-unused-vars
  const handleReviewAndCreate = () => {
    if (isFormValid()) {
      onSubmit();
    }
  };
  
  // Resale PayPal guard – block Next when default PM is not card for resale products
  const isResaleProduct =
    formData.configuration?.is_resale_product === true ||
    formData.type === 'resale' ||
    selectedProduct?.metadata?.resource_type === 'resale' ||
    !!selectedProduct?.metadata?.from_resale_resource;
  const resalePayPalBlocked = useMemo(() => {
    if (!isResaleProduct || !formData.cost_pool_id || !costPools?.length || isCurrentProductFree()) return false;
    const cp = costPools.find(c => c.id === formData.cost_pool_id);
    if (!cp) return false;
    const pms = cp.payment_methods;
    if (!Array.isArray(pms) || pms.length === 0) return false;
    const defaultPm = pms.find(pm => pm && pm.is_default);
    if (!defaultPm) return false;
    return defaultPm.type !== 'card';
  }, [isResaleProduct, formData.cost_pool_id, costPools, isCurrentProductFree]);

  const canProceedToNext = () => {
    if (currentStep === 0) { // Basics
      const hasName = formData.name && formData.name.trim();
      const hasTenant = formData.tenant_id && formData.tenant_id.trim();
      
      // Always require cost pool (unless product is free)
      // Price is always required (selected in Configuration step)
      const hasPrice = formData.configuration?.price_lookup_key || selectedPrice;
      const hasCostPool = !isCostPoolRequired || formData.cost_pool_id || isCurrentProductFree();
      return hasName && hasTenant && hasPrice && hasCostPool && !resalePayPalBlocked;
    }
    return true;
  };
  
  const handleNext = () => {
    // Prevent multiple submissions
    if (loading) {
      return;
    }
    if (canProceedToNext() && currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else if (canProceedToNext() && currentStep === steps.length - 1) {
      // If on Review step (last step), submit the form
      onSubmit();
    }
  };
  
  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };
  
  // eslint-disable-next-line no-unused-vars
  const handleStepClick = (index) => {
    // Only allow going to completed steps or next step if validation passes
    if (index <= currentStep || (index === currentStep + 1 && canProceedToNext())) {
      setCurrentStep(index);
    }
  };
  
  const steps = [
    {
      id: 'basics',
      label: t('tabs.basics', 'Basics'),
      content: (
        <div>
          <BasicsTab
            formData={formData}
            handleInputChange={handleInputChange}
            tenants={tenants}
            loadingTenants={loadingTenants}
            dropdownOpen={dropdownOpen}
            setDropdownOpen={setDropdownOpen}
            setFormData={setFormData}
            setIsSelectedTenantPartner={setIsSelectedTenantPartner}
            setIsDevTenant={setIsDevTenant}
            loadCostPools={loadCostPools}
            costPools={costPools}
            loadingCostPools={loadingCostPools}
            isDevTenant={isDevTenant}
            isSelectedTenantPartner={isSelectedTenantPartner}
            isDevResource={isDevResource}
            isCurrentProductFree={isCurrentProductFree}
            isCostPoolRequired={isCostPoolRequired}
            setIsDevResource={setIsDevResource}
            mode={mode}
            selectedProduct={selectedProduct}
            selectedPrice={selectedPrice}
            prices={prices}
            loadingPrices={loadingPrices}
            handleSubscriptionSelect={handleSubscriptionSelect}
            loadPrices={loadPrices}
            onClose={onClose}
            disabledTenantIds={tenantIdsWithResale}
          />
        </div>
      )
    },
    {
      id: 'review',
      label: t('tabs.review', 'Review + Create'),
      content: (
        <div>
          <ReviewTab
            formData={formData}
            selectedProduct={selectedProduct}
            selectedPrice={selectedPrice}
            tenants={tenants}
            costPools={costPools}
            isDevResource={isDevResource}
            isCostPoolRequired={isCostPoolRequired}
          />
        </div>
      )
    }
  ];
  
  const currentStepData = steps[currentStep] || null;
  
  return (
    <div className="resource-configuration-step">
      {/* Top-level Step Indicator */}
      <div className="marketplace-breadcrumb-with-steps">
        <div className="marketplace-breadcrumb">
          <span>{t('breadcrumb.home', 'Home')}</span>
          <span className="breadcrumb-separator">›</span>
          <span>{t('breadcrumb.createResource', 'Create Resource')}</span>
          <span className="breadcrumb-separator">›</span>
          <span className="breadcrumb-current">{currentStep === steps.length - 1 ? t('steps.review', 'Review') : t('steps.configuration', 'Configuration')}</span>
        </div>
        <div className="marketplace-steps-inline">
            <MarketplaceStepIndicator
              currentStep={currentStep === steps.length - 1 ? 3 : mainWizardStep}
              selectedProduct={selectedProduct}
              selectedPrice={selectedPrice}
            />
        </div>
        <div className="marketplace-wizard-toolbar">
          <button
            className="btn-back-to-marketplace-inline marketplace-wizard-toolbar-exit"
            onClick={onBack}
            type="button"
          >
            ← {t('buttons.back', 'Back')} {t('resourceConfiguration.toMarketplace', 'to Marketplace')}
          </button>
          <div className="marketplace-wizard-toolbar-nav">
            <button
              onClick={currentStep > 0 ? handlePrevious : undefined}
              disabled={currentStep === 0}
              className="btn-resource-back-inline btn-back-inline"
              type="button"
            >
              ← {t('buttons.previous', 'Back')}
            </button>
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('Create Resource button clicked', {
                  currentStep,
                  canProceed: canProceedToNext(),
                  loading,
                  stepsLength: steps.length,
                  isLastStep: currentStep === steps.length - 1
                });
                handleNext();
              }}
              disabled={!canProceedToNext() || loading}
              className="btn-resource-next-inline btn-next-inline"
              type="button"
            >
              {loading ? t('resourceConfiguration.creating', 'Creating...') : (currentStep === steps.length - 1 ? t('resourceConfiguration.createResource', 'Create Resource') : t('buttons.next', 'Next'))}
            </button>
          </div>
        </div>
      </div>
      
      {/* Wizard Configuration */}
      <div className={`template-wizard ${isDarkMode ? 'dark-theme' : 'light-theme'}`}>
        <div className="template-wizard-container">
          {/* Step Content */}
          <div className="template-wizard-content">
            {currentStepData && (
              <div className="template-wizard-step-content">
                {currentStepData.content}
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
};

